import React from 'react';
import { TableCell, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';

// Base table cell with minimal styling
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '6px 4px',
  fontSize: '0.875rem',
  textAlign: 'center',
  fontFamily: theme.typography.fontFamily,
  position: 'relative',
  color: theme.palette.mode === 'dark' ? theme.palette.text.primary : '#333333',
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: 'none',
  borderLeft: 'none',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  [theme.breakpoints.down('sm')]: {
    padding: '4px 2px',
    fontSize: '0.8rem'
  },
  '&.strike-cell': {
    fontWeight: 700,
    fontSize: '0.9rem',
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.85rem',
    }
  },
  '&.value-up': {
    color: theme.palette.success.main,
    fontWeight: 600
  },
  '&.value-down': {
    color: theme.palette.error.main,
    fontWeight: 600
  }
}));

// Clickable cell for LTP values
const ClickableCell = styled(TableCell)(({ theme }) => ({
  cursor: 'pointer',
  padding: '6px 4px',
  fontSize: '0.875rem',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontFamily: theme.typography.fontFamily,
  position: 'relative',
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.mode === 'dark' ? theme.palette.text.primary : '#333333',
  backgroundColor: 'transparent',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(0, 0, 0, 0.2)'
      : 'rgba(235, 235, 235, 0.7)'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '4px 2px',
    fontSize: '0.85rem'
  },
  '&.ltp-cell': {
    fontWeight: 600
  }
}));

// Helper function to format numbers
export const formatValue = (value, key) => {
  const numValue = parseFloat(value);

  if (key === 'change%') {
    if (isNaN(numValue)) return '0.00%';
    const sign = numValue > 0 ? '+' : '';
    return `${sign}${numValue.toFixed(2)}%`;
  }

  if (['oi', 'volume'].includes(key)) {
    if (isNaN(numValue)) return '0';
    if (numValue >= 1000000) {
      return (numValue / 1000000).toFixed(1) + 'M';
    } else if (numValue >= 1000) {
      return (numValue / 1000).toFixed(1) + 'K';
    }
    return numValue.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  if (['ltp', 'bid', 'ask', 'close'].includes(key)) {
    return isNaN(numValue) ? '0.00' : numValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  return value?.toString() || '0';
};

// Regular cell component
export const OptionCell = ({ 
  header, 
  value, 
  isITM, 
  isAtmRow, 
  valueClass
}) => {
  const key = header.toLowerCase();
  
  return (
    <StyledTableCell
      header={header}
      className={valueClass}
      sx={{
        fontWeight: isAtmRow ? 600 : 500,
        padding: '6px 2px'
      }}
    >
      {formatValue(value, key)}
    </StyledTableCell>
  );
};

// Clickable LTP cell component
export const LTPCell = ({ 
  header, 
  value, 
  isAtmRow, 
  valueClass, 
  side, 
  onClick,
  percentChange
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Determine text color for the percentage
  const percentageColor = percentChange > 0 
    ? theme.palette.success.main 
    : percentChange < 0 
      ? theme.palette.error.main 
      : theme.palette.text.secondary;
  
  return (
    <ClickableCell
      className={`ltp-cell ${side}-ltp`}
      onClick={onClick}
      sx={{
        fontWeight: isAtmRow ? 700 : 600,
        padding: isMobile ? '4px 2px' : '6px 4px',
        height: '42px',
        width: isMobile ? '120px' : 'auto',
        minWidth: isMobile ? '120px' : 'auto',
        display: 'table-cell',
        verticalAlign: 'middle',
        color: theme.palette.mode === 'dark' ? theme.palette.text.primary : '#333333',
        backgroundColor: 'transparent'
      }}
    >
      <div style={{ 
        fontWeight: 700, 
        fontSize: isMobile ? '0.8rem' : '1.1rem',
        marginBottom: isMobile ? '1px' : '2px',
        width: '100%',
        textAlign: 'center',
        color: theme.palette.mode === 'dark' ? theme.palette.text.primary : '#333333'
      }}>
        {formatValue(value, header.toLowerCase())}
      </div>
      {percentChange !== null && (
        <div 
          style={{ 
            fontSize: isMobile ? '0.6rem' : '0.85rem', 
            color: percentageColor,
            fontWeight: 600,
            width: '100%',
            textAlign: 'center'
          }}
        >
          {formatValue(percentChange, 'change%')}
        </div>
      )}
    </ClickableCell>
  );
};

// Header cell component
export const HeaderCell = ({ header, side }) => {
  const theme = useTheme();
  const isLTPColumn = header.toLowerCase() === 'ltp';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <StyledTableCell
      className={`${isLTPColumn ? `ltp-cell ${side}-ltp` : ''}`}
      sx={{
        bgcolor: theme.palette.mode === 'dark' 
          ? theme.palette.grey[900]
          : '#f5f5f5',
        color: theme.palette.mode === 'dark' 
          ? theme.palette.text.primary 
          : '#333333',
        borderBottom: `2px solid ${theme.palette.divider}`,
        padding: isMobile ? '8px 2px' : '8px 4px',
        fontWeight: 700,
        fontSize: isMobile ? '0.8rem' : '0.9rem'
      }}
    >
      {header}
    </StyledTableCell>
  );
};

// Strike price header cell
export const StrikeHeaderCell = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <StyledTableCell 
      className="strike-cell header-cell" 
      sx={{
        borderBottom: `2px solid ${theme.palette.primary.main}`,
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'dark' 
          ? theme.palette.grey[900]
          : '#f5f5f5',
        color: theme.palette.primary.main,
        fontWeight: 700,
        fontSize: isMobile ? '0.8rem' : '0.9rem',
        padding: isMobile ? '8px 2px' : '8px 4px',
        width: '90px'
      }}
    >
      Strike
    </StyledTableCell>
  );
};

export { StyledTableCell, ClickableCell }; 